import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons'
import {
  ModalConfirmV2,
  ModalSizeVariants,
  SearchBoxComponent,
} from '@labrav/react-components'
import { isEmpty } from 'lodash'
import { useParams } from 'react-router-dom'
import clsx from 'clsx'
import {
  isLoading,
  LoadingTypes,
  searchDropDownOptionMapping,
  searchDropDownOptions,
  useAccountId,
  useCurrentCloudProvider,
  useScreenSize,
} from '@labrav/common'

import { useDispatch, useSelector } from '../../../store'
import type { RootState } from '../../../store/types'
import { SubscriptionsDataTable } from '../SubscriptionsDataTable/SubscriptionsDataTable'
import { useStyles } from './SubscriptionSelectionModal.styles'
import { subscriptionListSortToApiParams } from '../../../utils/subscriptionsFilterMapping'
import type { SubscriptionSortConfig } from '../../../utils/subscriptionsFilterMapping'
import {
  clearModalSubscriptions,
  getModalSubscriptions,
} from '../../../modules/modalSubscription/action'
import { SubscriptionObject } from '../../../modules/subscription/reducer'

export const subscriptionModalmapping = {
  'Buyer & AWS account no': 'buyerCompanyName',
  'Product & ID': 'productName',
  'Contract ID': 'subscriptionId',
  Type: 'offerType',
  'Expiry date': 'contractExpirationDate',
}

export type SubscriptionSelectionModalProps = {
  open: boolean
  handleModalClose?: () => void
  acceptSelectedSubscription: (selectedSubscription: string) => void
  currentSelectedSubscriptionId?: string
  acceptLoading?: boolean
  type?: string
  offerType?: string
  hideCancelIcon?: boolean
  isSalesforce?: boolean
  salesforcePartnerId?: string
}

const getUpdatedSubscriptions = (
  subscriptions: SubscriptionObject[],
  type?: string
): SubscriptionObject[] => {
  if (type === 'CPPO') {
    return subscriptions.filter(sub => sub.offerType === 'CPPO')
  }

  if (type === 'PUBLIC_PRIVATE') {
    return subscriptions.filter(
      sub => sub.offerType === 'PRIVATE' || sub.offerType === 'PUBLIC'
    )
  }

  return subscriptions
}

export const SubscriptionSelectionModal = ({
  open,
  handleModalClose,
  acceptSelectedSubscription,
  currentSelectedSubscriptionId,
  acceptLoading = false,
  type,
  offerType,
  hideCancelIcon = false,
  isSalesforce = false,
  salesforcePartnerId = '',
}: SubscriptionSelectionModalProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { selectedProduct: productId } = useFlyOutProductsBasedOnSelectedCloud()
  const queryParams = new URLSearchParams(location.search)
  const privateOfferId = queryParams.get('privateOfferId') || ''
  const { accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId || salesforcePartnerId
  const { pageSize: pageSizeValue } = useScreenSize()
  const [selectedSubscription, setSelectedSubscription] = useState(
    currentSelectedSubscriptionId || ''
  )
  const [searchNow, setSearchNow] = useState(false)
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState('Company')
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const subscriptionSearchOptions = useMemo(
    () =>
      selectedCloudTypeProductListing === 'AWS'
        ? searchDropDownOptions.subsciptionsOptions
        : searchDropDownOptions.subsciptionsOptionsMultiCloud,
    [selectedCloudTypeProductListing]
  )

  useEffect(() => {
    setSelectedOption(
      selectedCloudTypeProductListing === 'AWS' ? 'Company' : 'All'
    )
  }, [selectedCloudTypeProductListing])

  const { pageNumber, subscriptions, count } = useSelector(
    (state: RootState) =>
      state.modalSubscription ?? {
        pageNumber: 0,
        subscriptions: [],
        count: 0,
      }
  )
  const [subscriptionListSortConfig, setSubscriptionListSortConfig] =
    useState<SubscriptionSortConfig | null>(null)
  const subscriptionListSortParams = useMemo(
    () =>
      subscriptionListSortToApiParams(
        selectedCloudTypeProductListing === 'GCP'
          ? subscriptionListSortConfig
          : undefined
      ),
    [subscriptionListSortConfig, selectedCloudTypeProductListing]
  )
  const updatedSubscriptions = getUpdatedSubscriptions(subscriptions, offerType)

  useEffect(() => {
    if (selectedCloudTypeProductListing !== 'GCP') {
      setSubscriptionListSortConfig(null)
    }
  }, [selectedCloudTypeProductListing])

  const onChangeCheckbox = (selectedItem: string[]) => {
    setSelectedSubscription(selectedItem[0])
  }
  const loading = useSelector((state: { loading?: unknown }) =>
    isLoading(state.loading as never)(LoadingTypes.GENERAL)
  )

  const isSelectedSubscriptionRenewalOn =
    (updatedSubscriptions || []).find(
      subscriptionItem =>
        subscriptionItem?.subscriptionId === selectedSubscription
    )?.renewal || false

  const handleAccept = async () => {
    await acceptSelectedSubscription(selectedSubscription)
  }
  const handleSearchClick = async () => {
    if (inputValue.trim() !== '') {
      setSearchNow(true)
      setShowCrossMark(true)
      await dispatch(clearModalSubscriptions())
      await dispatch(
        getModalSubscriptions({
          cloudMarketplace: selectedCloudTypeProductListing,
          partnerId: accountId,
          productId,
          pageNumber: 1,
          pageSize: pageSizeValue,
          input: inputValue,
          selectedInput: (
            searchDropDownOptionMapping.subsciptionsOptionMapping as Record<
              string,
              string
            >
          )[selectedOption],
          sectionName: 'agreements',
          ...subscriptionListSortParams,
        })
      )
    }
  }
  useEffect(() => {
    dispatch(clearModalSubscriptions())
    if (accountId) {
      if (!isEmpty(inputValue) && !isEmpty(selectedOption)) {
        dispatch(
          getModalSubscriptions({
            cloudMarketplace: selectedCloudTypeProductListing,
            partnerId: accountId,
            productId,
            pageNumber: 1,
            pageSize: pageSizeValue,
            input: inputValue,
            selectedInput: (
              searchDropDownOptionMapping.subsciptionsOptionMapping as Record<
                string,
                string
              >
            )[selectedOption || 'Company'],
            sectionName: 'agreements',
            ...subscriptionListSortParams,
          })
        )
      } else {
        dispatch(
          getModalSubscriptions({
            cloudMarketplace: selectedCloudTypeProductListing,
            partnerId: accountId,
            pageNumber: 1,
            pageSize: pageSizeValue,
            productId,
            privateOfferId,
            ...subscriptionListSortParams,
          })
        )
      }
    }

    return () => {
      dispatch(clearModalSubscriptions())
    }
  }, [
    accountId,
    productId,
    privateOfferId,
    searchNow,
    selectedCloudTypeProductListing,
    subscriptionListSortParams,
    dispatch,
    inputValue,
    pageSizeValue,
    selectedOption,
  ])

  return (
    <ModalConfirmV2
      title={'Select a contract'}
      content={
        <div className={classes.rootModal}>
          {selectedCloudTypeProductListing === 'AWS' ||
          selectedCloudTypeProductListing === 'GCP' ? (
            <SearchBoxComponent
              options={subscriptionSearchOptions}
              handleSearchClick={handleSearchClick}
              loading={loading}
              searchNow={searchNow}
              setSearchNow={setSearchNow}
              showCrossMark={showCrossMark}
              setShowCrossMark={setShowCrossMark}
              inputValue={inputValue}
              setInputValue={setInputValue}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            />
          ) : (
            <></>
          )}
          <div
            className={clsx(
              classes.root,
              isSalesforce ? classes.sfSearchedSpace : ''
            )}
            data-testid={'subscription-modal-content'}
          >
            <SubscriptionsDataTable
              mapping={subscriptionModalmapping}
              pageNumber={pageNumber}
              subscriptions={updatedSubscriptions}
              showRadioButton={true}
              onChangeCheckbox={onChangeCheckbox}
              selectedItem={selectedSubscription ? [selectedSubscription] : []}
              tableHeight={isSalesforce ? '324px' : '306px'}
              type={type}
              modalCount={count}
              searchNow={searchNow}
              inputValue={inputValue}
              selectedOption={selectedOption}
              privateOfferId={privateOfferId}
              isSalesforce={isSalesforce}
              salesforcePartnerId={salesforcePartnerId}
              sortConfig={
                selectedCloudTypeProductListing === 'GCP'
                  ? subscriptionListSortConfig
                  : null
              }
              onSortConfigChange={setSubscriptionListSortConfig}
            />
          </div>
        </div>
      }
      {...(isSelectedSubscriptionRenewalOn
        ? {
            bottomInfo:
              'If the auto renew is enabled you don’t need to renew the contract manually.',
          }
        : {})}
      open={open}
      titleAcceptButton={'Select'}
      variant={ModalSizeVariants.xLarge}
      onAccept={handleAccept}
      onDecline={handleModalClose || (() => void 0)}
      acceptDisabled={isEmpty(selectedSubscription)}
      hideCancelButton
      acceptLoading={acceptLoading}
      icon={
        <FontAwesomeIcon icon={faCircleCheck} className={classes.checkIcon} />
      }
      hideCancelIcon={hideCancelIcon}
    />
  )
}
